import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, JSX, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormikErrors, FormikTouched, Formik } from 'formik';
import useFetchAccountUsers from '@/hooks/useApi/useFetchAccountUsers';
import ApprovalRolesContext, { ApprovalRolesCtx } from './Dialogue/ApprovalRolesContext';
import ApprovalRolesDialogue from './Dialogue/ApprovalRolesDialogue';
import { ApprovalRolesDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import usePostCreateUserRolePermission from '@/hooks/useApi/usePostCreateUserRolePermission';
import usePostDeleteUserRolePermission from '@/hooks/useApi/usePostDeleteUserRolePermission';
import usePostUpdateUserRolePermission from '@/hooks/useApi/usePostUpdateUserRolePermission';
import usePostDeleteUserRole from '@/hooks/useApi/usePostDeleteUserRole';
import usePostCreateUserRole from '@/hooks/useApi/usePostCreateUserRole';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import { UtilButton } from 'UI';
import styles from './ApprovalRoles.module.scss';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';

const {
  Box,
  Title,
  Description,
  Button,
  ElementContainer,
  List,
  MultiSelector,
  TextField,
  Checkbox,
  DetailsCard,
  Icon,
} = vp;
type FC = FunctionComponent;
export type UserRolesUpdate = vp.UserRoles & {
  IsAny: boolean;
};
export type UserRolesUpdateExpanaded = {
  values: UserRolesUpdate;
  errors?: FormikErrors<UserRolesUpdate>;
  touched?: FormikTouched<UserRolesUpdate>;
  isValid?: boolean;
  submitForm?: () => Promise<void>;
};

export type GroupedUser = {
  username: string;
  amounts: Amount[];
};

export type Amount = {
  amount?: number;
  transactionTypes: string[];
  IsAny: boolean;
};
export type FormInfoGrouped = {
  errors?: FormikErrors<{ groupedUsers: GroupedUser[] }>;
  touched?: FormikTouched<{ groupedUsers: GroupedUser[] }>;
  isValid?: boolean;
  submitForm?: () => Promise<void>;
};
const UpdateRoles: FC = () => {
  const history = useHistory();
  const { userSettings } = useUserSettingsProvider();
  const { usersReset, transactionsReset, approvalListReset } = useApprovalWorkflow();
  const [fetchUsersResponse, fetchUsers] = useFetchAccountUsers({});
  const [usersAdmin, setUsersAdmin] = useState<UserRolesUpdateExpanaded[]>([
    {
      values: {
        USRUsers_ID: 0,
        UserRoles_ID: 0,
        Username: '',
        Role: '',
        MaxAmount: '0',
        TransactionType: '',
        IsAny: false,
      },
    },
  ]);
  const [usersAdminOriginal, setUsersAdminOriginal] = useState<UserRolesUpdateExpanaded[]>([]);
  const [, setUsersApprovers] = useState<UserRolesUpdateExpanaded[]>([
    {
      values: {
        USRUsers_ID: 0,
        UserRoles_ID: 0,
        Username: '',
        Role: '',
        MaxAmount: '0',
        TransactionType: '',
        IsAny: false,
      },
    },
  ]);
  const [usersApproversOriginal, setUsersApproversOriginal] = useState<UserRolesUpdateExpanaded[]>([]);
  const [approverSetting, setApproverSetting] = useState<FormInfoGrouped>({});
  const [, , createUserRole] = usePostCreateUserRole({});
  const [, , deleteUserRole] = usePostDeleteUserRole({});

  const [, , createUserRolePermission] = usePostCreateUserRolePermission({});
  const [, , deleteUserRolePermission] = usePostDeleteUserRolePermission({});
  const [, , updateUserRolePermission] = usePostUpdateUserRolePermission({});
  const [approvalRolesError, setApprovalRolesError] = useState('');
  const [showApprovalRolesErrorAlert, setShowApprovalRolesErrorAlert] = useState(false);
  const [showApprovalRolesSuccess, setShowApprovalRolesSuccess] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Roles.Update' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const approvalContext: ApprovalRolesCtx = {
    approvalRolesError,
    showApprovalRolesErrorAlert,
    showApprovalRolesSuccess,
  };

  const handlers = {
    handlApprovalRolesDialogue: (handlerProps: DialogueHandlerProps<ApprovalRolesDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: ApprovalRolesDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowApprovalRolesErrorAlert(false);
              setApprovalRolesError('');
            },
          },
        },
        success: {
          confirm: {
            default: () => {
              history.push('/transaction-approval/configuration/roles');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };
  const [userRolesResponse, fetchUserRoles] = useFetchUserRoles({});
  const [fetchAdmin, setFetchAdmin] = useState(true);
  const [adminUsers, setAdminUsers] = useState<vp.UserRolesResponse | undefined>();
  const [approverUsers, setApproverUsers] = useState<vp.UserRolesResponse | undefined>();
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [groupedUsers, setGroupedUsers] = useState<GroupedUser[]>([]);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const {
    response: { Country },
  } = useClientData();

  useEffect(() => {
    fetchTransactionTypes({
      IsWithdraw: 1,
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
    fetchUserRoles({
      Role: 'super admin',
    });
  }, []);
  useEffect(() => {
    if (userRolesResponse) {
      if (fetchAdmin) {
        fetchUserRoles({
          Role: 'approver',
        });
        setAdminUsers({ ...userRolesResponse });
        setFetchAdmin(false);
      } else {
        setApproverUsers({ ...userRolesResponse });
      }
    }
  }, [userRolesResponse]);

  useEffect(() => {
    if (adminUsers?.UserRolePermissions && Object.values(adminUsers.UserRolePermissions).length > 0) {
      setUsersAdmin(
        Object.values(adminUsers.UserRolePermissions).map((user) => ({
          values: { ...user, IsAny: user.MaxAmount === null },
        })),
      );
      setUsersAdminOriginal(
        Object.values(adminUsers.UserRolePermissions).map((user) => ({
          values: { ...user, IsAny: user.MaxAmount === null },
        })),
      );
    }
  }, [adminUsers]);

  const groupUsers = (users: UserRolesUpdateExpanaded[]): GroupedUser[] =>
    users.reduce((result, user) => {
      const username = user.values.Username;
      const amount = user.values.MaxAmount;
      const transactionType = user.values.TransactionType;
      const { IsAny } = user.values;
      // Find the existing grouped user in the result array
      const existingUser = result.find((groupedUser) => groupedUser.username === username);

      if (existingUser) {
        // If the user already exists, find the existing amount
        const existingAmount = existingUser.amounts.find(
          (existingAmountAmount) => existingAmountAmount.amount === Number(amount),
        );

        if (existingAmount) {
          // If the amount already exists, add the transactionType if it's not present
          if (!existingAmount.transactionTypes.includes(transactionType)) {
            existingAmount.transactionTypes.push(transactionType);
          }
        } else {
          // If the amount doesn't exist, create a new amount with the transactionType
          existingUser.amounts.push({
            amount: Number(amount),
            transactionTypes: [transactionType],
            IsAny,
          });
        }
      } else {
        // If the user doesn't exist, create a new user with the amount and transactionType
        result.push({
          username,
          amounts: [
            {
              amount: Number(amount),
              transactionTypes: [transactionType],
              IsAny,
            },
          ],
        });
      }

      return result;
    }, [] as GroupedUser[]);
  useEffect(() => {
    if (approverUsers?.UserRolePermissions) {
      const approvers = Object.values(approverUsers.UserRolePermissions).map((user) => ({
        values: { ...user, IsAny: user.MaxAmount === null },
      }));
      setUsersApprovers(approvers);
      setUsersApproversOriginal(approvers);

      const tempGroupedUsers = groupUsers(approvers);
      if (tempGroupedUsers.length === 0) {
        tempGroupedUsers.push({
          username: '',
          amounts: [
            {
              amount: 0,
              transactionTypes: [],
              IsAny: false,
            },
          ],
        });
      }
      setGroupedUsers(tempGroupedUsers);
    }
  }, [approverUsers]);
  useEffect(() => {
    fetchUsers({
      Timezone: userSettings.timezone,
    });
  }, []);
  const superAdminRole = async () => {
    const usersAdminDeleted = usersAdminOriginal.filter(
      (original) => !usersAdmin.some((userAdmin) => userAdmin.values.Username === original.values.Username),
    );

    const usersAdminToCreate = usersAdmin.filter(
      (userAdmin) => !usersAdminOriginal.some((original) => original.values.Username === userAdmin.values.Username),
    );

    // Delete users
    for (const deletedUser of usersAdminDeleted) {
      await deleteUserRole({ Username: deletedUser.values.Username, Role: 'super admin' });
    }

    // Create users
    for (const user of usersAdminToCreate) {
      await createUserRole({ Username: user.values.Username, Role: 'super admin' });
    }
  };
  const approvalRole = async () => {
    // Iterate through the originalGroups to find differences
    const originalGroups = groupUsers(usersApproversOriginal);
    for (const originalGroup of originalGroups) {
      // Find the corresponding group in groups
      const correspondingGroup = groupedUsers.find((group) => group.username === originalGroup.username);

      if (correspondingGroup) {
        // Check for differences in amounts
        for (let indexAmount = 0; indexAmount < originalGroup.amounts.length; indexAmount += 1) {
          const originalAmount = originalGroup.amounts[indexAmount];
          const correspondingAmount = correspondingGroup.amounts[indexAmount];
          // Check for changes in transactionTypes
          const addedTransactionTypes = correspondingAmount.transactionTypes.filter(
            (type) => !originalAmount.transactionTypes.includes(type),
          );
          const removedTransactionTypes = originalAmount.transactionTypes.filter(
            (type) => !correspondingAmount.transactionTypes.includes(type),
          );
          // Check for changes in amount
          if (
            originalAmount.amount !== correspondingAmount.amount ||
            correspondingAmount.IsAny !== originalAmount.IsAny
          ) {
            // Call updateUserRolePermission or other relevant function for amount change
            for (const transactionType of originalAmount.transactionTypes) {
              await updateUserRolePermission({
                Username: correspondingGroup.username,
                Role: 'approver',
                TransactionType: transactionType,
                MaxAmount: correspondingAmount.IsAny ? 1 : correspondingAmount.amount,
                SetNoMaxAmount: correspondingAmount.IsAny,
              });
            }
          }

          // Call relevant functions for added and removed transactionTypes
          for (const addedType of addedTransactionTypes) {
            await createUserRolePermission({
              Username: correspondingGroup.username,
              Role: 'approver',
              TransactionType: addedType,
              MaxAmount: correspondingAmount.IsAny ? 1 : correspondingAmount.amount,
              SetNoMaxAmount: correspondingAmount.IsAny,
            });
          }

          for (const removedType of removedTransactionTypes) {
            await deleteUserRolePermission({
              Username: correspondingGroup.username,
              Role: 'approver',
              TransactionType: removedType,
            });
          }
        }
        const newAmounts = correspondingGroup.amounts.slice(originalGroup.amounts.length);

        for (const amounts of newAmounts) {
          for (const transactionType of amounts.transactionTypes) {
            await createUserRolePermission({
              Username: correspondingGroup.username,
              Role: 'approver',
              TransactionType: transactionType,
              MaxAmount: amounts.IsAny ? 1 : amounts.amount,
              SetNoMaxAmount: amounts.IsAny,
            });
          }
        }
      } else {
        // The group does not exist in the updated groups, call usePostDeleteUserRole
        await deleteUserRole({
          Username: originalGroup.username,
          Role: 'approver',
        });
      }
    }
    // Check for new groups in the updated groups
    for (const group of groupedUsers) {
      const originalGroup = originalGroups.find((original) => original.username === group.username);

      if (!originalGroup) {
        // The group is new, call usePostCreateUserRole
        await createUserRole({ Username: group.username, Role: 'approver' });
        for (let indexAmount = 0; indexAmount < group.amounts.length; indexAmount += 1) {
          const amounts = group.amounts[indexAmount];
          for (const transactionType of amounts.transactionTypes) {
            await createUserRolePermission({
              Username: group.username,
              Role: 'approver',
              TransactionType: transactionType,
              MaxAmount: amounts.IsAny ? 1 : amounts.amount,
              SetNoMaxAmount: amounts.IsAny,
            });
          }
        }
      }
    }
  };
  const processEnpoints = async () => {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    await superAdminRole();
    await approvalRole();

    usersReset();
    transactionsReset();
    approvalListReset();
    setDataFetchingInfo(false);
    setShowApprovalRolesSuccess(true);
  };
  const validationSchema = Yup.object().shape({
    groupedUsers: Yup.array().of(
      Yup.object().shape({
        username: Yup.string().required(),
        amounts: Yup.array().of(
          Yup.object().shape({
            amount: Yup.number()
              .nullable()

              .when('IsAny', {
                is: false,
                then: Yup.number().required().min(1, t('Validation.amountPositiveError')),
              }),
            IsAny: Yup.boolean(),
            transactionTypes: Yup.array().of(Yup.string()).min(1, t('Validation.requiredTransactionTypes')),
          }),
        ),
      }),
    ),
  });
  return (
    <div>
      <ApprovalRolesContext.Provider value={approvalContext}>
        <Box flx="col" mb="md" gapSize="16px">
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace('/transaction-approval/configuration/roles');
              }
            }}
          >
            {tGlobal('Buttons.back')}
          </UtilButton>

          <Title title={t('title')} />
        </Box>
        <Box mt="md" mb="md">
          <Description>{t('instructions')}</Description>
        </Box>

        <ElementContainer
          label={t('SuperAdminSetup.title')}
          padding="0px 0 0px"
          input={
            <Box flx="col" width="100%">
              <Description size="md" color="#5E5E5E">
                {t('SuperAdminSetup.instructions')}
              </Description>
              <Box maxwidth="480px" flx="col">
                <DetailsCard
                  width="100%"
                  content={
                    <List
                      skipDelete={usersAdmin.length > 1 ? 0 : 1}
                      addItemText={t('SuperAdminSetup.AddAdditionalSuperAdmin.label')}
                      id="admin-list"
                      hasAdd={false}
                      options={usersAdmin.map((user, index) => ({
                        value: index.toString(),
                        label: (
                          <Formik
                            initialValues={{ [`Username-${index}`]: user.values.Username }}
                            onSubmit={() => {}}
                            validationSchema={Yup.object().shape({
                              [`Username-${index}`]: Yup.string().required(),
                            })}
                            enableReinitialize
                          >
                            {({ values, touched, errors, handleChange, handleBlur, submitForm, isValid }) => {
                              useEffect(() => {
                                const updatedArray = [...usersAdmin];
                                updatedArray[index].touched = touched;
                                updatedArray[index].errors = errors;
                                updatedArray[index].submitForm = submitForm;
                                updatedArray[index].isValid = isValid;
                                setUsersAdmin(updatedArray);
                              }, [errors, values, isValid]);
                              useEffect(() => {
                                handleChange(vp.createSyntheticEvent(`Username-${index}`, values[`Username-${index}`]));
                              }, []);
                              return (
                                <div className={styles.listItemContainer}>
                                  <MultiSelector
                                    items={Object.values(fetchUsersResponse.Users || {})
                                      .filter((filterUser) => {
                                        const UserNames = usersAdmin
                                          .map((useradmins) => useradmins.values.Username)
                                          .join('|')
                                          .split('|');
                                        const CurrentUserNames = user.values.Username.split('|');
                                        return (
                                          CurrentUserNames.includes(filterUser.Username) ||
                                          !UserNames.includes(filterUser.Username)
                                        );
                                      })
                                      .map((userList) => ({
                                        label: userList.Username,
                                        value: userList.Username,
                                        checked: user.values.Username.split('|').includes(userList.Username),
                                      }))}
                                    identifier={`superAdmin-${index}`}
                                    variant="outlined"
                                    labelPosition="right"
                                    labelSize="16px"
                                    height="37px"
                                    indicatorMinHeight="34px"
                                    value={user.values.Username}
                                    name={`Username-${index}`}
                                    placeholder={t('SuperAdminSetup.AddAdditionalSuperAdmin.placeholder')}
                                    onClose={() => {
                                      const updatedArray = [...usersAdmin];
                                      const value = updatedArray[index].values.Username;
                                      const valuesArray = value.split('|');
                                      valuesArray.forEach((selectedValue, valuesIndex) => {
                                        if (valuesIndex > 0) {
                                          updatedArray.push({
                                            values: {
                                              USRUsers_ID: 0,
                                              UserRoles_ID: 0,
                                              Username: selectedValue,
                                              Role: '',
                                              MaxAmount: '0',
                                              TransactionType: '',
                                              IsAny: false,
                                            },
                                          });
                                        } else {
                                          updatedArray[index].values.Username = valuesArray[0] || '';
                                        }
                                      });

                                      if (!valuesArray[0] && updatedArray.length > 1) {
                                        setUsersAdmin(usersAdmin.filter((_, i) => i !== index));
                                      } else {
                                        setUsersAdmin(updatedArray);
                                      }
                                    }}
                                    onBlur={handleBlur}
                                    error={
                                      errors[`Username-${index}`] && touched[`Username-${index}`]
                                        ? errors[`Username-${index}`]
                                        : null
                                    }
                                    setFieldValue={(name, value) => {
                                      const updatedArray = [...usersAdmin];
                                      handleChange(vp.createSyntheticEvent(name, value));
                                      updatedArray[index].values.Username = value;
                                      updatedArray[index].touched = touched;
                                      updatedArray[index].errors = errors;
                                      updatedArray[index].submitForm = submitForm;
                                      updatedArray[index].isValid = isValid;
                                      setUsersAdmin(updatedArray);
                                    }}
                                  />
                                </div>
                              );
                            }}
                          </Formik>
                        ),
                      }))}
                      onDelete={(id: string) => {
                        const index = Number(id);

                        setUsersAdmin(usersAdmin.filter((_, i) => i !== index));
                      }}
                    />
                  }
                />
              </Box>
            </Box>
          }
        />

        {usersAdmin.length !== Object.values(fetchUsersResponse.Users || {}).length && (
          <Box gap="sm" pt="md">
            <Button
              id="add-item-list"
              label={t('SuperAdminSetup.AddAdditionalSuperAdmin.label')}
              icon="add"
              color="none"
              noShadow
              iconLocation="left"
              iconHeight="12px"
              iconWidth="12px"
              noPadding
              onClick={() => {
                setUsersAdmin([
                  ...usersAdmin,
                  {
                    values: {
                      USRUsers_ID: 0,
                      UserRoles_ID: 0,
                      Username: '',
                      Role: '',
                      MaxAmount: '0',
                      TransactionType: '',
                      IsAny: false,
                    },
                  },
                ]);
              }}
            />
          </Box>
        )}
        <Box maxwidth="1250px" flx="col" mb="lg">
          <ElementContainer
            label={t('ApproverSetup.title')}
            padding="40px 0 0px"
            input={
              <Box flx="col" width="100%">
                <Box pb="sm">
                  <Description size="md" color="#5E5E5E">
                    {t('ApproverSetup.instructions')}
                  </Description>
                </Box>
                <Formik
                  enableReinitialize
                  initialValues={{ groupedUsers: [...groupedUsers] }}
                  onSubmit={() => {}}
                  validationSchema={validationSchema}
                  validateOnChange
                  validateOnBlur
                  validateOnMount
                >
                  {({ values, touched, errors, isValid, submitForm, handleChange, handleBlur, validateForm }) => {
                    useEffect(() => {
                      validateForm();
                      setApproverSetting({
                        submitForm,
                        errors,
                        touched,
                        isValid,
                      });
                    }, [errors, values, isValid]);
                    useEffect(() => {
                      validateForm();
                      setApproverSetting({
                        submitForm,
                        errors,
                        touched,
                        isValid,
                      });
                    }, []);
                    useEffect(() => {
                      validateForm();
                    }, [groupedUsers]);

                    return (
                      <List
                        skipDelete={groupedUsers.length > 1 ? 0 : 1}
                        hasAdd={groupedUsers.length !== Object.values(fetchUsersResponse.Users || {}).length}
                        addItemText={t('ApproverSetup.addAdditionalApproverButton')}
                        varient="card"
                        id="appover-list"
                        deletePosition="top"
                        positionX="0px"
                        positionY="35px"
                        options={groupedUsers.map((user, index) => ({
                          value: index.toString(),
                          label: (
                            <Box gap="common" flx="row" ai="fs" jc="sb" width="100%">
                              <div
                                style={{ width: index === 0 && groupedUsers.length === 1 ? 'calc(40% + 24px)' : '40%' }}
                              >
                                <ElementContainer
                                  isBold={false}
                                  width="100%"
                                  label={t('ApproverSetup.Approver.label')}
                                  padding="0px 0 8px"
                                  input={
                                    <MultiSelector
                                      items={Object.values(fetchUsersResponse.Users || {}).reduce(
                                        (result, value) => {
                                          const username = value.Username;
                                          const isUsernameNotInGroups = !groupedUsers.some(
                                            (groupedUser) => groupedUser.username === username,
                                          );

                                          // If the username is not in groups, add it to the result
                                          if (isUsernameNotInGroups || username === user.username) {
                                            result.push({
                                              label: username,
                                              value: username,
                                              checked: groupedUsers[index]?.username.includes(username),
                                            });
                                          }

                                          return result;
                                        },
                                        [] as { label: string; value: string; checked: boolean }[],
                                      )}
                                      labelPosition="right"
                                      labelSize="16px"
                                      variant="outlined"
                                      height="37px"
                                      indicatorMinHeight="34px"
                                      value={user.username}
                                      placeholder={t('ApproverSetup.Approver.placeholder')}
                                      name="username"
                                      identifier={`groupedUsers.${index}`}
                                      onBlur={(e) => {
                                        handleBlur(e);
                                      }}
                                      onClose={() => {
                                        const updatedArray = [...groupedUsers];
                                        const value = updatedArray[index]?.username;
                                        const valuesArray = value.split('|');
                                        valuesArray.forEach((selectedValue, valuesIndex) => {
                                          if (valuesIndex > 0) {
                                            updatedArray.push({
                                              username: selectedValue,
                                              amounts: [
                                                {
                                                  amount: 0,
                                                  transactionTypes: [],
                                                  IsAny: false,
                                                },
                                              ],
                                            });
                                          } else {
                                            updatedArray[index].username = valuesArray[0] || '';
                                            handleChange(
                                              vp.createSyntheticEvent(
                                                `groupedUsers.${index}.username`,
                                                updatedArray[index].username,
                                              ),
                                            );
                                          }
                                        });

                                        if (!valuesArray[0] && updatedArray.length > 1) {
                                          setGroupedUsers(groupedUsers.filter((_, i) => i !== index));
                                        } else {
                                          setGroupedUsers(updatedArray);
                                        }
                                      }}
                                      setFieldValue={(_feild: string, value: string) => {
                                        const updatedArray = [...groupedUsers];
                                        handleChange(
                                          vp.createSyntheticEvent(`groupedUsers.${index}.username`, value.split('|')),
                                        );
                                        updatedArray[index].username = value;
                                        setGroupedUsers(updatedArray);
                                      }}
                                      error={
                                        errors?.groupedUsers &&
                                        errors?.groupedUsers[index] &&
                                        (errors?.groupedUsers[index] as FormikErrors<GroupedUser>).username &&
                                        touched.groupedUsers &&
                                        touched.groupedUsers[index] &&
                                        touched.groupedUsers[index].username
                                          ? (errors?.groupedUsers[index] as FormikErrors<GroupedUser>).username
                                          : null
                                      }
                                    />
                                  }
                                />
                              </div>
                              <div style={{ width: index === 0 ? 'calc(60% - 24px)' : '60%' }}>
                                <List
                                  skipDelete={2}
                                  addItemText={t('ApproverSetup.Approver.newMaximumApprovalAmountButton')}
                                  id="admin-list"
                                  hasAdd={
                                    user.amounts
                                      .map((amount) => {
                                        // Check if amount.transactionTypes is an array
                                        if (Array.isArray(amount.transactionTypes)) {
                                          return amount.transactionTypes.join('; ');
                                        }
                                        // Handle the case where transactionTypes is not an array (or undefined)
                                        return ''; // You can adjust the default value as needed
                                      })
                                      .filter((x) => !!x)
                                      .join('; ')
                                      .split('; ').length !==
                                    Object.values(transactionTypesResponse?.TransactionTypes || {}).length
                                  }
                                  onAdd={() => {
                                    const updatedArray = [...groupedUsers];
                                    updatedArray[index].amounts.push({
                                      amount: 0,
                                      transactionTypes: [],
                                      IsAny: false,
                                    });
                                    setGroupedUsers(updatedArray);
                                  }}
                                  onDelete={(id: string) => {
                                    const updatedArray = [...groupedUsers];
                                    const Id = Number(id);
                                    updatedArray[index].amounts = groupedUsers[index].amounts.filter(
                                      (_, i) => i !== Id,
                                    );
                                    setGroupedUsers(updatedArray);
                                  }}
                                  options={amountsUser(index, user, errors, touched, handleChange, handleBlur)}
                                />
                              </div>
                            </Box>
                          ),
                        }))}
                        onDelete={(id: string) => {
                          const index = Number(id);
                          setGroupedUsers([...groupedUsers.filter((_, i) => i !== index)]);
                        }}
                        onAdd={() => {
                          const updatedArray = [...groupedUsers];

                          updatedArray.push({
                            username: '',
                            amounts: [
                              {
                                amount: 0,
                                transactionTypes: [],
                                IsAny: false,
                              },
                            ],
                          });

                          setGroupedUsers([...updatedArray]);
                        }}
                      />
                    );
                  }}
                </Formik>
              </Box>
            }
          />
        </Box>
        <Box jc="fe">
          <Box gap="sm">
            <Button
              id="approval-update-roles"
              label={t('SaveButton.label')}
              color="primary"
              isDisabled={approverSetting?.isValid === false || usersAdmin.some((user) => user.isValid === false)}
              onClick={() => {
                const submitForm = async () => {
                  setDataFetchingInfo(true, t('SaveButton.loadingMessage'), 'fullScreen');
                  if (approverSetting?.submitForm) {
                    await approverSetting?.submitForm();
                  }

                  const numberOfErrorAdmin = usersAdmin.reduce((accumulator, currentValue) => {
                    if (currentValue && currentValue.errors) {
                      return accumulator + Object.keys(currentValue.errors).length;
                    }
                    return accumulator;
                  }, 0);

                  if (numberOfErrorAdmin > 0 || Object.values(approverSetting.errors || {}).length > 0) {
                    usersAdmin.forEach((user) => {
                      if (user && user.submitForm) {
                        user.submitForm();
                      }
                    });
                  } else {
                    await processEnpoints();
                  }
                  setDataFetchingInfo(false);
                };
                submitForm();
              }}
            />
          </Box>
        </Box>
        <ApprovalRolesDialogue handleDialogue={handlers.handlApprovalRolesDialogue} />
      </ApprovalRolesContext.Provider>
    </div>
  );
  function amountsUser(
    index: number,
    user: GroupedUser,
    errors: FormikErrors<{
      groupedUsers: GroupedUser[];
    }>,
    touched: FormikTouched<{
      groupedUsers: GroupedUser[];
    }>,
    handleChange: {
      (e: React.ChangeEvent<any>): void;
      <T = string | React.ChangeEvent<any>>(
        field: T,
      ): T extends React.ChangeEvent<any> ? void : (e: string | React.ChangeEvent<any>) => void;
    },
    handleBlur: {
      (e: React.FocusEvent<any, Element>): void;
      <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
    },
  ): {
    value: string;
    label: JSX.Element;
  }[] {
    const combinedTransactionTypes = user.amounts
      .map((amount) => {
        // Check if amount.transactionTypes is an array
        if (Array.isArray(amount.transactionTypes)) {
          return amount.transactionTypes.join('; ');
        }
        // Handle the case where transactionTypes is not an array (or undefined)
        return ''; // You can adjust the default value as needed
      })
      .filter((x) => !!x);
    const amounts = user.amounts.map((amount, indexAmount) => ({
      value: indexAmount.toString(),
      label: (
        <Box gap="common" flx="row" ai="fs" jc="sb" width="100%">
          <div style={{ width: indexAmount > 0 ? 'calc(40% - 23px)' : '40%' }}>
            <ElementContainer
              isBold={false}
              width={indexAmount > 0 ? '248px' : '271px'}
              label={user.amounts.length > 1 ? '' : t('ApproverSetup.PaymentRails.label')}
              padding={user.amounts.length > 1 ? '0px 0 0px' : '0px 0 8px'}
              input={
                <MultiSelector
                  name="appover-rails"
                  identifier={`groupedUsers.${index}.amounts.${indexAmount}.transactionTypes`}
                  value={amount.transactionTypes.join('|')}
                  variant="outlined"
                  items={Object.values(transactionTypesResponse?.TransactionTypes || {})
                    .filter(
                      (transactionType) =>
                        !combinedTransactionTypes.join('; ').split('; ').includes(transactionType.TransactionType) ||
                        amount.transactionTypes.includes(transactionType.TransactionType),
                    )
                    .map((transactionType) => ({
                      label: transactionType.TransactionType,
                      value: transactionType.TransactionType,
                      checked: amount.transactionTypes.includes(transactionType.TransactionType),
                    }))}
                  labelPosition="right"
                  labelSize="16px"
                  placeholder={t('ApproverSetup.PaymentRails.placeholder')}
                  height="37px"
                  indicatorMinHeight="34px"
                  selectAllLabel={
                    user.amounts.length === 1 &&
                    Object.values(transactionTypesResponse?.TransactionTypes || {})
                      .filter(
                        (transactionType) =>
                          !combinedTransactionTypes.join('; ').split('; ').includes(transactionType.TransactionType) ||
                          amount.transactionTypes.includes(transactionType.TransactionType),
                      )
                      .map((transactionType) => ({
                        label: transactionType.TransactionType,
                        checked: amount.transactionTypes.includes(transactionType.TransactionType),
                      }))
                      .every((transactionBox) => transactionBox.checked)
                      ? t('ApproverSetup.PaymentRails.selectAllLabel')
                      : undefined
                  }
                  isSelectAll={user.amounts.length === 1}
                  selectAllName={t('ApproverSetup.PaymentRails.selectAllName')}
                  setFieldValue={(_feild: string, value: string) => {
                    const updatedArray = [...groupedUsers];
                    handleChange(
                      vp.createSyntheticEvent(
                        `groupedUsers.${index}.amounts.${indexAmount}.transactionTypes`,
                        value.split('|'),
                      ),
                    );
                    updatedArray[index].amounts[indexAmount].transactionTypes = value.split('|');
                    setGroupedUsers(updatedArray);
                  }}
                  error={
                    errors?.groupedUsers &&
                    errors?.groupedUsers[index] &&
                    ((errors?.groupedUsers[index] as FormikErrors<GroupedUser>).amounts as FormikErrors<Amount[]>) &&
                    ((errors?.groupedUsers[index] as FormikErrors<GroupedUser>).amounts as FormikErrors<Amount[]>)[
                      indexAmount
                    ] &&
                    ((errors?.groupedUsers[index] as FormikErrors<GroupedUser>).amounts as FormikErrors<Amount[]>)[
                      indexAmount
                    ]?.transactionTypes &&
                    touched.groupedUsers &&
                    touched.groupedUsers[index] &&
                    touched.groupedUsers[index].amounts &&
                    (touched.groupedUsers[index].amounts as FormikTouched<Amount>[])[indexAmount] &&
                    (touched.groupedUsers[index].amounts as FormikTouched<Amount>[])[indexAmount].transactionTypes
                      ? (((errors?.groupedUsers[index] as FormikErrors<GroupedUser>).amounts as FormikErrors<Amount[]>)[
                          indexAmount
                        ]?.transactionTypes as string | null)
                      : null
                  }
                  onBlur={() => {}}
                />
              }
            />
          </div>
          <div style={{ width: indexAmount > 0 ? 'calc(60% + 23px)' : '60%' }}>
            <ElementContainer
              isBold={false}
              width="100%"
              label={user.amounts.length > 1 ? '' : t('ApproverSetup.MaximumApprovalAmount.label')}
              padding={user.amounts.length > 1 ? '0px 0 0px' : '0px 0 8px'}
              input={
                <Box ai="center">
                  <TextField
                    width="100%"
                    height="37px"
                    containerWidth="200px"
                    variant="outlined"
                    preset={amount.IsAny ? 'text' : 'number'}
                    value={amount.IsAny ? 'Any' : amount.amount}
                    name={`groupedUsers.${index}.amounts.${indexAmount}.amount`}
                    onBlur={(e) => {
                      handleBlur(e);
                    }}
                    disabled={amount.IsAny}
                    elementLeft={
                      !amount.IsAny ? (
                        <Box ps="sm">
                          <Icon icon="dollar" position="start" color="medium-grey" size="sm" width="17px" />
                        </Box>
                      ) : undefined
                    }
                    textColor={amount.IsAny ? 'grey' : 'black'}
                    onChange={(e) => {
                      handleChange(e);
                      const updatedArray = [...groupedUsers];
                      updatedArray[index].amounts[indexAmount].amount = Number(e.target.value);
                      setGroupedUsers(updatedArray);
                    }}
                    error={
                      errors?.groupedUsers &&
                      errors?.groupedUsers[index] &&
                      ((errors?.groupedUsers[index] as FormikErrors<GroupedUser>).amounts as FormikErrors<Amount[]>) &&
                      ((errors?.groupedUsers[index] as FormikErrors<GroupedUser>).amounts as FormikErrors<Amount[]>)[
                        indexAmount
                      ] &&
                      ((errors?.groupedUsers[index] as FormikErrors<GroupedUser>).amounts as FormikErrors<Amount[]>)[
                        indexAmount
                      ]?.amount &&
                      !groupedUsers[index].amounts[indexAmount].IsAny &&
                      touched.groupedUsers &&
                      touched.groupedUsers[index] &&
                      touched.groupedUsers[index].amounts &&
                      (touched.groupedUsers[index].amounts as FormikTouched<Amount>[])[indexAmount] &&
                      (touched.groupedUsers[index].amounts as FormikTouched<Amount>[])[indexAmount].amount
                        ? (
                            (errors?.groupedUsers[index] as FormikErrors<GroupedUser>).amounts as FormikErrors<Amount[]>
                          )[indexAmount]?.amount
                        : null
                    }
                  />
                  <Checkbox
                    labelSize="14px"
                    labelPosition="right"
                    name={`appover-rails-${index}-${indexAmount}-approver-no-limit`}
                    value={amount.IsAny}
                    label={t('ApproverSetup.MaximumApprovalAmount.ApproverHasNoLimitCheckbox')}
                    onBlur={() => {}}
                    onChange={(e) => {
                      const updatedArray = [...groupedUsers];
                      handleChange(
                        vp.createSyntheticEvent(`groupedUsers.${index}.amounts.${indexAmount}.IsAny`, e.target.checked),
                      );
                      handleChange(vp.createSyntheticEvent(`groupedUsers.${index}.amounts.${indexAmount}.amount`, 0));
                      updatedArray[index].amounts[indexAmount].IsAny = e.target.checked;
                      updatedArray[index].amounts[indexAmount].amount = 0;
                      setGroupedUsers(updatedArray);
                    }}
                  />
                </Box>
              }
            />
          </div>
        </Box>
      ),
    }));
    if (user.amounts.length > 1) {
      amounts.unshift({
        value: '0',
        label: (
          <div style={{ width: 'calc(40% + 214px)' }}>
            <ElementContainer
              isBold={false}
              width="100%"
              label={t('ApproverSetup.PaymentRails.label')}
              padding="0px 0 8px"
              input={
                <TextField
                  width="100%"
                  containerWidth="100%"
                  disabled
                  height="37px"
                  variant="background"
                  textColor="grey"
                  value={
                    combinedTransactionTypes.length === 0
                      ? t('ApproverSetup.PaymentRails.noneSelectedLabel')
                      : combinedTransactionTypes.join('; ').split('; ').length ===
                          Object.values(transactionTypesResponse?.TransactionTypes || {}).length
                        ? t('ApproverSetup.PaymentRails.selectAllLabel')
                        : combinedTransactionTypes.join('; ')
                  }
                  name={`admin-${index}`}
                  onBlur={() => {}}
                  onChange={() => {}}
                />
              }
            />
          </div>
        ),
      });
    }
    return amounts;
  }
};
export default UpdateRoles;
